/**
 * The single shared date formatter. Every clinical timestamp in the interface
 * goes through here.
 *
 * Format is always `YYYY-MM-DD HH:mm`, 24-hour, **regardless of interface
 * language**. This is a patient safety requirement, not a presentation
 * preference: `09/24/2026` versus `24/09/2026` is unacceptable ambiguity in a
 * record that may be read by staff from several countries during a crisis.
 *
 * See docs/I18N.md §3 and docs/DECISIONS.md #14.
 *
 * `toLocaleString` and `toLocaleDateString` are forbidden on clinical values
 * (AGENTS.md rule #7). They are locale-dependent, host-dependent, and have
 * changed output between runtime versions.
 */

/** Thrown rather than rendering a wrong or blank timestamp. */
export class InvalidClinicalDateError extends Error {
  constructor(value: unknown) {
    super(`Not a valid date: ${String(value)}`)
    this.name = 'InvalidClinicalDateError'
  }
}

function pad(value: number, length: number): string {
  return String(value).padStart(length, '0')
}

function toDate(value: Date | string | number): Date {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) {
    throw new InvalidClinicalDateError(value)
  }
  return date
}

/**
 * `YYYY-MM-DD HH:mm` in the viewer's local time zone.
 *
 * Local time is correct here: the ward reads wall-clock time, and the hospital
 * is in one time zone. The stored value is always `timestamptz`, so the
 * conversion is well defined.
 */
export function formatClinicalDateTime(value: Date | string | number): string {
  const date = toDate(value)
  return (
    `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1, 2)}-${pad(date.getDate(), 2)}` +
    ` ${pad(date.getHours(), 2)}:${pad(date.getMinutes(), 2)}`
  )
}

/**
 * The inverse of {@link formatClinicalDateTime}: a typed `YYYY-MM-DD HH:mm` as the local instant it
 * names, or null if it does not name exactly one.
 *
 * For a clinician *entering* a clinical time, so that what they type is the format they read
 * everywhere else (AGENTS.md §5) — not the browser's `datetime-local` control, which renders in
 * the browser's locale: `mm/dd/yyyy --:-- AM/PM` on an English-US workstation. Introduced
 * 2026-10-07 for the medication end date, where the browser pass found exactly that control.
 *
 * **Strict, and it repairs nothing.** One space, no seconds, no `T`, a 24-hour clock. A day that
 * does not exist (`2026-02-30`), an hour of `24`, and a wall-clock time inside the spring
 * daylight-saving gap — which `Date` would silently move an hour on — are all null, by checking that
 * the constructed date reads back as exactly what was typed. A caller that gets null refuses; it
 * does not guess.
 */
export function parseClinicalDateTime(typed: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/.exec(typed)
  if (match === null) {
    return null
  }

  const [year, month, day, hours, minutes] = match.slice(1).map(Number) as [
    number,
    number,
    number,
    number,
    number,
  ]
  const date = new Date(year, month - 1, day, hours, minutes)

  const readsBack =
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day &&
    date.getHours() === hours &&
    date.getMinutes() === minutes

  return readsBack ? date : null
}

/**
 * `YYYY-MM-DD` for a value that is **already** a calendar date and never had a
 * time of day — a date of birth as the API sends it.
 *
 * **There is deliberately no day-precision formatter that takes a `Date`.** The
 * obvious one — construct a `Date` and read `getFullYear()`/`getMonth()`/`getDate()`
 * off it, the way {@link formatClinicalDateTime} does — is a trap for exactly this
 * input: `new Date('1938-03-03')` parses as midnight **UTC**, and `getDate()` then
 * reads the viewer's local day, so west of Greenwich it prints `1938-03-02`. A date
 * of birth is how a clinician confirms they have the right patient, and one day off
 * is the kind of wrongness that looks like a different person's record. One such
 * function existed here with no callers until 2026-10-01; it was removed rather than
 * left for somebody to reach for.
 *
 * So no `Date` is constructed at all. The input is validated and returned, which
 * is the whole job — the API already sends the format I18N.md §3 requires. It
 * throws rather than passing something else through, for the same reason the dose
 * formatter does: a malformed date rendered as-is is a date somebody will read.
 */
export function formatClinicalDateOnly(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (match === null) {
    throw new InvalidClinicalDateError(value)
  }

  // A well-formed string can still name a day that does not exist (2026-02-30),
  // and Date's constructor would silently roll it into March.
  const [, year, month, day] = match
  const utc = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))
  if (
    utc.getUTCFullYear() !== Number(year) ||
    utc.getUTCMonth() !== Number(month) - 1 ||
    utc.getUTCDate() !== Number(day)
  ) {
    throw new InvalidClinicalDateError(value)
  }

  return value
}

/**
 * `YYYY-MM-DD HH:mm:ss` in UTC, for audit and other legal output.
 *
 * Audit timestamps are shown in UTC on purpose: they are evidence, and evidence
 * should not shift with the reader's machine. Seconds are included because
 * ordering matters in an access review.
 */
export function formatAuditTimestampUtc(value: Date | string | number): string {
  const date = toDate(value)
  return (
    `${pad(date.getUTCFullYear(), 4)}-${pad(date.getUTCMonth() + 1, 2)}-${pad(date.getUTCDate(), 2)}` +
    ` ${pad(date.getUTCHours(), 2)}:${pad(date.getUTCMinutes(), 2)}:${pad(date.getUTCSeconds(), 2)} UTC`
  )
}
